import {
  Create,
  TextInput,
  NumberInput,
  SelectInput,
  FormDataConsumer,
  SaveButton,
  required,
} from "react-admin";
import { WizardForm, useWizardFormContext } from "@react-admin/ra-form-layout";
import { useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, Card, CardContent, Box, Grid, Typography, Divider, Button } from "@mui/material";
import ComponentEdit from "../../../components/PageComponents/ComponentEdit";
import { useCurrentProject } from "../../../contexts/AccountProjectContext";
import { useSyncProjectIdToForm } from "../../../hooks/useSyncProjectIdToForm";
import FormField from "../../../components/Common/FormField";

const travelRuleOwnerChoices = [
  { id: "self", name: "Self" },
  { id: "third_party", name: "Third party" },
];

const travelRuleOwnerTypeChoices = [
  { id: "person", name: "Person" },
  { id: "business", name: "Business" },
];

const travelRuleHostTypeChoices = [
  { id: "platform", name: "Platform / Exchange" },
  { id: "wallet", name: "Wallet" },
  { id: "unknown", name: "Unknown" },
];

const platformHostChoices = [
  { id: "binance", name: "Binance" },
  { id: "coinbase", name: "Coinbase" },
  { id: "kraken", name: "Kraken" },
  { id: "bitstamp", name: "Bitstamp" },
  { id: "bybit", name: "Bybit" },
  { id: "okx", name: "OKX" },
  { id: "crypto.com", name: "Crypto.com" },
  { id: "other", name: "Other (specify)" },
];

const walletHostChoices = [
  { id: "self_hosted", name: "Self-hosted wallet (e.g. MetaMask, Ledger)" },
  { id: "fireblocks", name: "Fireblocks" },
  { id: "bitgo", name: "BitGo" },
  { id: "copper", name: "Copper" },
  { id: "other", name: "Other (specify)" },
];

// Half width from md up; `fullWidth` spans the row for long values.
const FieldRow = ({ label, children, fullWidth = false }: { label: any; children: any; fullWidth?: boolean }) => (
  <Grid item xs={12} md={fullWidth ? 12 : 6}>
    <FormField label={label}>{children}</FormField>
  </Grid>
);

const ProjectSelectionAlert = () => {
  const { t }: { t: any } = useTranslation();
  const { errors } = useFormState();
  const errorMessageProject = Boolean(errors?.projectId);

  if (!errorMessageProject) return null;

  return (
    <Grid item xs={12}>
      <Alert severity="error">
        {t(
          "Entity selection is not configured to create a gas station transfer. Please select another entity on top of the page or update the configuration in the entity settings."
        )}
      </Alert>
    </Grid>
  );
};

const BasicInformationStep = () => {
  const { t }: { t: any } = useTranslation();
  const { name, currentProject } = useCurrentProject();
  const gsParams = (currentProject as any)?.parameters?.gasStation || {};
  const blockchains = (currentProject as any)?.parameters?.blockchains;
  const blockchain = blockchains?.length ? blockchains[0] : (currentProject as any)?.parameters?.blockchain;
  const network = (currentProject as any)?.parameters?.network;
  const currency = gsParams.currency;
  const limitPer24H = gsParams.limitPer24H;
  const approvalThresholdEur = gsParams.approvalThresholdEur;

  return (
    <Grid container spacing={3}>
      <ProjectSelectionAlert />

      <Grid item xs={12} sx={{ display: "none" }}>
        <TextInput source="projectId" label={name} fullWidth disabled />
      </Grid>

      <FieldRow label={t("Entity")}>
        <Typography fontWeight={800} fontSize={16}>
          {name || t("No entity selected")}
        </Typography>
      </FieldRow>

      <FieldRow label={t("Internal reference")}>
        <TextInput source="internalRef" label={t("Internal reference")} fullWidth validate={required()} />
      </FieldRow>

      <FieldRow label={t("Amount")}>
        <NumberInput
          source="amount"
          label={t("Amount")}
          fullWidth
          validate={required()}
          helperText={
            limitPer24H
              ? t("24h limit for this entity: {{limit}} {{currency}}", { limit: limitPer24H, currency })
              : false
          }
        />
      </FieldRow>

      <FieldRow label={t("Currency")}>
        {currency ? (
          <Typography fontWeight={800} fontSize={16}>
            {currency}
          </Typography>
        ) : (
          <SelectInput
            source="currency"
            label={t("Currency")}
            fullWidth
            validate={required()}
            choices={["ETH", "POL", "MATIC", "XTZ"].map((c) => ({ id: c, name: c }))}
            helperText={t("This entity has no default currency configured: select one.")}
          />
        )}
        <TextInput source="currency" label="" defaultValue={currency} sx={{ display: "none" }} />
      </FieldRow>

      <FieldRow label={t("Destination address")} fullWidth>
        <TextInput source="toAddress" label={t("Destination address")} fullWidth validate={required()} />
      </FieldRow>

      <FieldRow label={t("Blockchain")}>
        <Typography fontWeight={800} fontSize={16}>
          {blockchain || t("Not configured on this entity")}
        </Typography>
        <TextInput source="blockchain" label="" defaultValue={blockchain} sx={{ display: "none" }} />
      </FieldRow>

      <FieldRow label={t("Network")}>
        <Typography fontWeight={800} fontSize={16}>
          {network || t("Not configured on this entity")}
        </Typography>
        <TextInput source="network" label="" defaultValue={network} sx={{ display: "none" }} />
      </FieldRow>

      {approvalThresholdEur && (
        <Grid item xs={12}>
          <Alert severity="info">
            {t(
              "Transfers above ~{{threshold}} € (estimated at send time) require operator approval before they are executed.",
              { threshold: approvalThresholdEur }
            )}
          </Alert>
        </Grid>
      )}

      <FormDataConsumer>
        {({ formData }) =>
          limitPer24H && formData?.amount > limitPer24H && (
            <Grid item xs={12}>
              <Alert severity="error">
                {t(
                  "Amount exceeds this entity's 24h limit ({{limit}} {{currency}}). Please reduce the amount.",
                  { limit: limitPer24H, currency }
                )}
              </Alert>
            </Grid>
          )
        }
      </FormDataConsumer>
    </Grid>
  );
};

const TravelRuleStep = () => {
  const { t }: { t: any } = useTranslation();

  return (
    <Grid container spacing={3}>
      <ProjectSelectionAlert />

      <Grid item xs={12}>
        <Alert severity="info">
          {t(
            "The Travel rule requires crypto service providers to collect and share information about the sender and the destination of a transfer, to help prevent money laundering and terrorist financing. Fill in who owns the funds and who hosts the destination address."
          )}
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {t("Sender (owner of the funds)")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("Who owns the funds being transferred: this entity itself, or a third party on whose behalf it acts?")}
        </Typography>
      </Grid>

      <FieldRow label={t("Owner")} fullWidth>
        <SelectInput
          source="travelRule.owner"
          label={t("Owner")}
          fullWidth
          choices={travelRuleOwnerChoices}
          validate={required()}
        />
      </FieldRow>

      <FormDataConsumer>
        {({ formData }) =>
          formData?.travelRule?.owner === "third_party" && (
            <>
              <FieldRow label={t("Owner type")}>
                <SelectInput
                  source="travelRule.ownerType"
                  label={t("Owner type")}
                  fullWidth
                  choices={travelRuleOwnerTypeChoices}
                  validate={required()}
                  helperText={t("Is the third-party owner a natural person or a business?")}
                />
              </FieldRow>
              <FieldRow label={t("Owner name")}>
                <TextInput
                  source="travelRule.ownerInfos.name"
                  label={t("Owner name")}
                  fullWidth
                  validate={required()}
                  helperText={t("Full name or legal entity name of the third-party owner")}
                />
              </FieldRow>
            </>
          )
        }
      </FormDataConsumer>

      <Grid item xs={12} sm={12} md={12} mt={2} mb={1}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 0.5, mt: 2 }}>
          {t("Destination (host of the address)")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("Who controls the destination address: a known platform/exchange, a wallet, or unknown?")}
        </Typography>
      </Grid>

      <FieldRow label={t("Host type")}>
        <SelectInput
          source="travelRule.hostType"
          label={t("Host type")}
          fullWidth
          choices={travelRuleHostTypeChoices}
          validate={required()}
        />
      </FieldRow>

      <FormDataConsumer>
        {({ formData }) => {
          const hostType = formData?.travelRule?.hostType;
          if (hostType === "platform") {
            return (
              <FieldRow label={t("Platform")}>
                <SelectInput
                  source="travelRule.hostChoice"
                  label={t("Platform")}
                  fullWidth
                  choices={platformHostChoices}
                  validate={required()}
                />
              </FieldRow>
            );
          }
          if (hostType === "wallet") {
            return (
              <FieldRow label={t("Wallet provider")}>
                <SelectInput
                  source="travelRule.hostChoice"
                  label={t("Wallet provider")}
                  fullWidth
                  choices={walletHostChoices}
                  validate={required()}
                />
              </FieldRow>
            );
          }
          return null;
        }}
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          ["platform", "wallet"].includes(formData?.travelRule?.hostType) &&
          formData?.travelRule?.hostChoice === "other" && (
            <FieldRow label={t("Host name")} fullWidth>
              <TextInput
                source="travelRule.host"
                label={t("Host name")}
                fullWidth
                validate={required()}
                helperText={t("Name of the platform or wallet provider hosting the destination address")}
              />
            </FieldRow>
          )
        }
      </FormDataConsumer>
    </Grid>
  );
};

const CustomToolbar = () => {
  const { t }: { t: any } = useTranslation();
  const { hasNextStep, hasPreviousStep, goToNextStep, goToPreviousStep } = useWizardFormContext();

  return (
    <Box display="flex" justifyContent="flex-end" gap={1} m={2}>
      {hasPreviousStep && (
        <Button onClick={goToPreviousStep} variant="outlined">
          {t("Back")}
        </Button>
      )}
      {hasNextStep ? (
        <Button onClick={goToNextStep} variant="contained" color="primary">
          {t("Next")}
        </Button>
      ) : (
        <SaveButton label={t("Create transfer")} variant="contained" color="primary" />
      )}
    </Box>
  );
};

const GasStationCreate = () => {
  const { t }: { t: any } = useTranslation();
  const { typeProject, currentProject } = useCurrentProject();
  const projectId = useSyncProjectIdToForm();
  const gsParams = (currentProject as any)?.parameters?.gasStation || {};

  const validateTransfer = (values: any) => {
    const errors: any = {};
    if (typeProject !== "gasStation") {
      errors.projectId = t(
        "Entity selection is not configured to create a gas station transfer. Please select another entity on top of the page or update the configuration in the entity settings."
      );
    }
    if (!values.internalRef) {
      errors.internalRef = t("Internal reference is required");
    }
    if (!values.amount) {
      errors.amount = t("Amount is required");
    }
    if (!values.currency) {
      errors.currency = t("Currency is required");
    }
    if (!values.toAddress) {
      errors.toAddress = t("Destination address is required");
    }
    if (gsParams.limitPer24H && values.amount > gsParams.limitPer24H) {
      errors.amount = t("Amount exceeds this entity's 24h limit");
    }
    if (
      ["platform", "wallet"].includes(values.travelRule?.hostType) &&
      values.travelRule?.hostChoice !== "other" &&
      !values.travelRule?.hostChoice
    ) {
      errors["travelRule.hostChoice"] = t("Please select a host");
    }
    return errors;
  };

  const transformData = (data: any) => {
    const { hostChoice, ...travelRule } = data.travelRule || {};
    if (hostChoice && hostChoice !== "other") {
      const chosen =
        platformHostChoices.find((c) => c.id === hostChoice) ||
        walletHostChoices.find((c) => c.id === hostChoice);
      travelRule.host = chosen?.name || hostChoice;
    }
    return { ...data, projectId, travelRule };
  };

  return (
    <ComponentEdit title={t("New gas station transfer")} subtitle={t("Send crypto from the entity's gas station to a destination address.")}>
      <Card>
        <Box p={3} display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">
              {t("Transfer details")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("The transfer first, then the Travel rule information.")}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <CardContent sx={{ p: 4 }}>
          <Create redirect="show" transform={transformData}>
            <WizardForm validate={validateTransfer} toolbar={<CustomToolbar />}>
              <WizardForm.Step label={t("Transfer")}>
                <BasicInformationStep />
              </WizardForm.Step>
              <WizardForm.Step label={t("Travel rule")}>
                <TravelRuleStep />
              </WizardForm.Step>
            </WizardForm>
          </Create>
        </CardContent>
      </Card>
    </ComponentEdit>
  );
};

export default GasStationCreate;
